import { z } from 'zod';
import { RejectCode } from './errors.ts';
import { EmoteId } from './emote.ts';
import { CharacterSlotId, DisplayName, ParticipantRole, SelfInfo } from './participant.ts';
import { DeadlinePublic, InstanceStatus, ModuleManifest, TeamAssignment } from './module.ts';
import { SessionPublic, SessionSettings } from './session.ts';
import { VoiceCredentials } from './voice.ts';
import { PROTOCOL_VERSION, SHORT_CODE_REGEX } from './constants.ts';

// ---------------------------------------------------------------------------
// Session-level (infrastructure) actions. Module actions are validated by the module's own actionSchema.
// ---------------------------------------------------------------------------

export const SessionActionSchemas = {
  'session.set_ready': z.object({ ready: z.boolean() }),
  'session.set_character': z.object({ characterSlot: CharacterSlotId }),
  'session.set_muted': z.object({ muted: z.boolean() }),
  'session.emote': z.object({ emoteId: EmoteId }),
  'session.leave': z.object({}),
  /** Host: launch a root module from the lobby with explicit participants (defaults to every ready player). */
  'session.launch': z.object({
    moduleId: z.string(),
    participantIds: z.array(z.string()).optional(),
    teams: z.array(TeamAssignment).nullable().optional(),
    config: z.unknown().optional(),
    /** Presenter sessions only; live sessions ignore it and seed from crypto. */
    seed: z.number().int().optional(),
  }),
  'session.pause': z.object({ detail: z.string().max(200).optional() }),
  'session.resume': z.object({}),
  /** Host: release a disconnected (or any) participant's slot; modules are told via onParticipantEvent. */
  'session.remove_participant': z.object({ participantId: z.string() }),
  /** Host: cancel the active child; the parent receives an abandoned result exactly once. */
  'session.abandon_child': z.object({}),
  /** Host: end the session; the active root receives abandoned status. */
  'session.end': z.object({}),
  /** Host: after results, keep the room and return everyone to the lobby. */
  'session.return_to_lobby': z.object({}),
  /** Host: invalidate the current invitation link/QR/short code and issue new ones. */
  'session.rotate_invite': z.object({}),
  /** Host: rebuild an ordinary room as a clean lobby, retaining only the host identity and credential. */
  'session.reset_for_group': z.object({}),
  /** Presenter only: rebuild the current fixture in place (same session id, new instances). */
  'presenter.reset': z.object({}),
} as const;
export type SessionActionName = keyof typeof SessionActionSchemas;
export const SessionActionName = z.enum(Object.keys(SessionActionSchemas) as [SessionActionName, ...SessionActionName[]]);

// ---------------------------------------------------------------------------
// WebSocket messages
// ---------------------------------------------------------------------------

export const ActionTarget = z.union([z.literal('session'), z.object({ instanceId: z.string() })]);
export type ActionTarget = z.infer<typeof ActionTarget>;

export const ClientHello = z.object({
  type: z.literal('hello'),
  protocol: z.literal(PROTOCOL_VERSION),
  sessionId: z.string(),
  /** Participant reconnect token or presenter token. Never a display name. */
  token: z.string().min(16),
  client: z.object({ kind: z.enum(['player', 'display', 'presenter']) }).optional(),
});

export const ClientAction = z.object({
  type: z.literal('action'),
  /** Client-generated unique ID (UUID). Retries reuse it; the server answers duplicates with the original outcome. */
  actionId: z.string().min(8).max(64),
  target: ActionTarget,
  /** When present, the action is rejected as `stale_version` unless it equals the current session version. */
  expectedVersion: z.number().int().nonnegative().optional(),
  name: z.string().min(1).max(64),
  payload: z.unknown(),
  /** Presenter connections only, on presenter sessions only, targeting an existing simulated participant; otherwise `forbidden`. */
  asParticipantId: z.string().optional(),
});

export const ClientResync = z.object({ type: z.literal('resync') });
export const ClientPing = z.object({ type: z.literal('ping'), t: z.number() });
export const ClientPresenterView = z.object({
  type: z.literal('presenter_view'),
  viewer: z.union([
    z.object({ kind: z.literal('participant'), participantId: z.string() }),
    z.object({ kind: z.literal('spectator') }),
  ]),
});

export const ClientMessage = z.discriminatedUnion('type', [
  ClientHello,
  ClientAction,
  ClientResync,
  ClientPing,
  ClientPresenterView,
]);
export type ClientMessage = z.infer<typeof ClientMessage>;
export type ClientActionMessage = z.infer<typeof ClientAction>;

export const InstanceView = z.object({
  instanceId: z.string(),
  moduleId: z.string(),
  moduleVersion: z.string(),
  moduleName: z.string(),
  renderer: z.string(),
  status: InstanceStatus,
  /** Module projection for this viewer. Opaque to the infrastructure; validated by the module renderer. */
  view: z.unknown(),
  deadlines: z.array(DeadlinePublic),
  isParticipant: z.boolean(),
});
export type InstanceView = z.infer<typeof InstanceView>;

/** The full authorised snapshot for one connection. Sent on welcome, after every accepted transition and on resync. */
export const ClientView = z.object({
  version: z.number().int().nonnegative(),
  serverTime: z.number(),
  session: SessionPublic,
  you: SelfInfo.nullable(),
  /** Presenter perspective switching: participant ID or null for spectator/own view. */
  viewingAs: z.string().nullable(),
  root: InstanceView.nullable(),
  child: InstanceView.nullable(),
});
export type ClientView = z.infer<typeof ClientView>;

export const ServerWelcome = z.object({
  type: z.literal('welcome'),
  protocol: z.literal(PROTOCOL_VERSION),
  serverTime: z.number(),
  self: SelfInfo,
  view: ClientView,
});
export const ServerState = z.object({ type: z.literal('state'), view: ClientView });
export const ServerEvent = z.object({
  type: z.literal('event'),
  eventId: z.string(),
  /** Session version at emission; clients may drop events older than their snapshot. */
  version: z.number().int(),
  at: z.number(),
  instanceId: z.string().nullable(),
  name: z.string(),
  payload: z.unknown(),
});
export const ServerAck = z.object({ type: z.literal('ack'), actionId: z.string(), version: z.number().int() });
export const ServerReject = z.object({
  type: z.literal('reject'),
  actionId: z.string(),
  code: RejectCode,
  message: z.string(),
  version: z.number().int(),
  /** True when re-reading state and sending a NEW action may succeed. Rejections are never cached; only accepted outcomes are. */
  retryable: z.boolean(),
});
export const ServerError = z.object({ type: z.literal('error'), code: RejectCode, message: z.string() });
export const ServerPong = z.object({ type: z.literal('pong'), t: z.number(), serverTime: z.number() });
export const ServerClosed = z.object({
  type: z.literal('closed'),
  reason: z.enum(['superseded', 'removed', 'left', 'session_ended', 'unauthenticated', 'expired', 'server_shutdown']),
  message: z.string(),
});

export const ServerMessage = z.discriminatedUnion('type', [
  ServerWelcome,
  ServerState,
  ServerEvent,
  ServerAck,
  ServerReject,
  ServerError,
  ServerPong,
  ServerClosed,
]);
export type ServerMessage = z.infer<typeof ServerMessage>;
export type ServerEventMessage = z.infer<typeof ServerEvent>;

// ---------------------------------------------------------------------------
// HTTP DTOs
// ---------------------------------------------------------------------------

export const CreateSessionRequest = z.object({
  displayName: DisplayName,
  characterSlot: CharacterSlotId.optional(),
  hostIsPlayer: z.boolean().default(true),
  settings: SessionSettings.pick({ maxPlayers: true, maxSpectators: true, voiceMode: true, lateJoin: true }).partial().optional(),
});
export type CreateSessionRequest = z.infer<typeof CreateSessionRequest>;
export type CreateSessionRequestInput = z.input<typeof CreateSessionRequest>;

export const ParticipantCredentials = z.object({
  participantId: z.string(),
  /** Reconnect token. Store client-side per session; never appears in invitation links or QR codes. */
  token: z.string(),
  role: ParticipantRole,
  isHost: z.boolean(),
});
export type ParticipantCredentials = z.infer<typeof ParticipantCredentials>;

export const CreateSessionResponse = z.object({
  sessionId: z.string(),
  shortCode: z.string(),
  inviteToken: z.string(),
  inviteUrl: z.string(),
  wsUrl: z.string(),
  participant: ParticipantCredentials,
});
export type CreateSessionResponse = z.infer<typeof CreateSessionResponse>;

export const InviteInfo = z.object({
  sessionId: z.string(),
  inviteToken: z.string(),
  status: z.enum(['lobby', 'running', 'ended']),
  hostName: z.string(),
  players: z.number().int(),
  maxPlayers: z.number().int(),
  spectators: z.number().int(),
  maxSpectators: z.number().int(),
  availableSlots: z.array(CharacterSlotId),
  canJoinAsPlayer: z.boolean(),
  canJoinAsSpectator: z.boolean(),
  lateJoin: SessionSettings.shape.lateJoin,
  voiceMode: SessionSettings.shape.voiceMode,
});
export type InviteInfo = z.infer<typeof InviteInfo>;

export const ResolveCodeRequest = z.object({ code: z.string().trim().toUpperCase().regex(SHORT_CODE_REGEX) });

export const JoinRequest = z.object({
  displayName: DisplayName,
  role: z.enum(['player', 'spectator']).default('player'),
  characterSlot: CharacterSlotId.optional(),
});
export type ResolveCodeRequest = z.infer<typeof ResolveCodeRequest>;
export type JoinRequest = z.infer<typeof JoinRequest>;
export type JoinRequestInput = z.input<typeof JoinRequest>;

export const JoinResponse = z.object({
  sessionId: z.string(),
  wsUrl: z.string(),
  participant: ParticipantCredentials,
});
export type JoinResponse = z.infer<typeof JoinResponse>;

export const InviteDetails = z.object({
  inviteUrl: z.string(),
  inviteToken: z.string(),
  shortCode: z.string(),
  /** SVG markup encoding exactly `inviteUrl`. */
  qrSvg: z.string(),
  /** Reachable-address caveat surfaced to the host (e.g. localhost is not phone-ready). */
  reachability: z.enum(['localhost', 'lan', 'public']),
});
export type InviteDetails = z.infer<typeof InviteDetails>;

export const ModulesResponse = z.object({ modules: z.array(ModuleManifest) });

export const VoiceCredentialsResponse = z.object({
  credentials: VoiceCredentials,
  /** Human-readable note, e.g. "Simulated voice: no provider configured". */
  note: z.string().nullable(),
});
export type VoiceCredentialsResponse = z.infer<typeof VoiceCredentialsResponse>;
export type ModulesResponse = z.infer<typeof ModulesResponse>;

export const PresenterCreateRequest = z.object({
  presenterKey: z.string().optional(),
  fixtureId: z.string(),
  seed: z.number().int().optional(),
  presenterName: DisplayName.default('Presenter'),
});
export type PresenterCreateRequest = z.infer<typeof PresenterCreateRequest>;
export type PresenterCreateRequestInput = z.input<typeof PresenterCreateRequest>;
export const PresenterCreateResponse = z.object({
  sessionId: z.string(),
  presenterToken: z.string(),
  wsUrl: z.string(),
  fixtureId: z.string(),
  seed: z.number().int(),
  simulatedParticipantIds: z.array(z.string()),
});
export type PresenterCreateResponse = z.infer<typeof PresenterCreateResponse>;

export const FixturesResponse = z.object({
  fixtures: z.array(z.object({ id: z.string(), name: z.string(), description: z.string(), moduleId: z.string() })),
});
export type FixturesResponse = z.infer<typeof FixturesResponse>;

export const ErrorResponse = z.object({ code: RejectCode, message: z.string() });
